/**
 * Utilidades de normalización centralizadas para todo el proyecto
 * Maneja conversiones bidireccionales entre diferentes formatos de naming convention
 */

import moment from 'moment';

/**
 * Función universal para convertir entre naming conventions
 */
export const convertNamingConvention = {
  /**
   * Convierte snake_case a PascalCase
   */
  snakeToPascal: (obj) => {
    if (!obj || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(convertNamingConvention.snakeToPascal);
    
    const converted = {};
    Object.keys(obj).forEach(key => {
      const pascalKey = key.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase())
                          .replace(/^[a-z]/, match => match.toUpperCase());
      converted[pascalKey] = typeof obj[key] === 'object' ? 
        convertNamingConvention.snakeToPascal(obj[key]) : obj[key];
    });
    return converted;
  },

  /**
   * Convierte PascalCase a snake_case
   */
  pascalToSnake: (obj) => {
    if (!obj || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(convertNamingConvention.pascalToSnake);
    
    const converted = {};
    Object.keys(obj).forEach(key => {
      const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '');
      converted[snakeKey] = typeof obj[key] === 'object' ? 
        convertNamingConvention.pascalToSnake(obj[key]) : obj[key];
    });
    return converted;
  },

  /**
   * Convierte camelCase a snake_case
   */
  camelToSnake: (obj) => {
    if (!obj || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(convertNamingConvention.camelToSnake);
    
    const converted = {};
    Object.keys(obj).forEach(key => {
      const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
      converted[snakeKey] = typeof obj[key] === 'object' ? 
        convertNamingConvention.camelToSnake(obj[key]) : obj[key];
    });
    return converted;
  },

  /**
   * Convierte snake_case a camelCase
   */
  snakeToCamel: (obj) => {
    if (!obj || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(convertNamingConvention.snakeToCamel);
    
    const converted = {};
    Object.keys(obj).forEach(key => {
      const camelKey = key.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase());
      converted[camelKey] = typeof obj[key] === 'object' ? 
        convertNamingConvention.snakeToCamel(obj[key]) : obj[key];
    });
    return converted;
  }
};

/**
 * Función universal de normalización que agrega todas las variantes de naming
 */
export const normalizeDataObject = (obj, entityType = 'generic') => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(item => normalizeDataObject(item, entityType));

  // Crear objeto normalizado con todas las variantes
  const normalized = { ...obj };

  // Para cada propiedad, agregar las variantes en diferentes naming conventions
  Object.keys(obj).forEach(key => {
    const value = obj[key];
    
    // Generar todas las variantes de la clave
    const variants = generateKeyVariants(key);
    
    // Agregar todas las variantes al objeto normalizado
    variants.forEach(variant => {
      if (!normalized.hasOwnProperty(variant)) {
        normalized[variant] = value;
      }
    });
  });

  // Aplicar normalizaciones específicas por tipo de entidad
  return applyEntitySpecificNormalization(normalized, entityType);
};

/**
 * Genera todas las variantes de naming convention para una clave
 */
export const generateKeyVariants = (key) => {
  const variants = [key]; // Incluir la clave original
  
  // snake_case variants
  const snakeCase = key.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '');
  if (!variants.includes(snakeCase)) variants.push(snakeCase);
  
  // camelCase variants
  const camelCase = key.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase());
  if (!variants.includes(camelCase)) variants.push(camelCase);
  
  // PascalCase variants
  const pascalCase = camelCase.replace(/^[a-z]/, match => match.toUpperCase());
  if (!variants.includes(pascalCase)) variants.push(pascalCase);
  
  return variants;
};

/**
 * Aplica normalizaciones específicas según el tipo de entidad
 */
export const applyEntitySpecificNormalization = (obj, entityType) => {
  const normalized = { ...obj };

  switch (entityType) {
    case 'empresa':
    case 'empresas':
      return normalizeEmpresaData(normalized);
    
    case 'usuario':
    case 'usuarios':
      return normalizeUsuarioData(normalized);
    
    case 'cliente':
    case 'clientes':
      return normalizeClienteData(normalized);
    
    case 'vehiculo':
    case 'vehiculos':
      return normalizeVehiculoData(normalized);
    
    case 'cita':
    case 'citas':
      return normalizeCitaData(normalized);
    
    case 'servicio':
    case 'servicios':
      return normalizeServicioData(normalized);
    
    case 'repuesto':
    case 'repuestos':
      return normalizeRepuestoData(normalized);
    
    case 'mecanico':
    case 'mecanicos':
      return normalizeMecanicoData(normalized);
    
    case 'sucursal':
    case 'sucursales':
      return normalizeSucursalData(normalized);
    
    default:
      return normalizeGenericData(normalized);
  }
};

/**
 * Normalización específica para datos de empresa
 */
export const normalizeEmpresaData = (empresa) => {
  const normalized = { ...empresa };
  
  // Mapeos específicos de empresa
  const mappings = {
    // ID mappings
    id: ['id', 'Id', 'empresa_id', 'EmpresaId', 'EmpresaID'],
    
    // Basic info
    nombre: ['nombre', 'Nombre', 'name', 'Name'],
    rut: ['rut', 'Rut', 'RUT'],
    email: ['email', 'Email', 'correo', 'Correo'],
    telefono: ['telefono', 'Telefono', 'phone', 'Phone'],
    direccion: ['direccion', 'Direccion', 'address', 'Address'],
    
    // Business info
    razon_social: ['razon_social', 'RazonSocial', 'razonSocial'],
    giro: ['giro', 'Giro', 'business_type', 'BusinessType'],
    codigo_empresa: ['codigo_empresa', 'CodigoEmpresa', 'company_code', 'CompanyCode'],
    
    // Plan and subscription
    plan_id: ['plan_id', 'PlanId', 'PlanID'],
    subscription_status: ['subscription_status', 'SubscriptionStatus'],
    trial_ends_at: ['trial_ends_at', 'TrialEndsAt'],
    
    // User relationship data
    perfil_id: ['perfil_id', 'PerfilId', 'profile_id', 'ProfileId'],
    permisos: ['permisos', 'Permisos', 'permissions', 'Permissions'],
    permisos_custom: ['permisos_custom', 'PermisosCustom', 'custom_permissions', 'CustomPermissions'],
    es_superadmin: ['es_superadmin', 'EsSuperAdmin', 'is_superadmin', 'IsSuperAdmin'],
    puede_acceder_multiples_empresas: ['puede_acceder_multiples_empresas', 'PuedeAccederMultiplesEmpresas', 'can_access_multiple_companies'],
    relacion_activa: ['relacion_activa', 'RelacionActiva', 'active_relationship', 'ActiveRelationship'],
    fecha_union: ['fecha_union', 'FechaUnion', 'join_date', 'JoinDate'],
    
    // Status
    activa: ['activa', 'Activa', 'active', 'Active'],
    
    // Timestamps
    fecha_registro: ['fecha_registro', 'FechaRegistro', 'created_at', 'CreatedAt'],
    updated_at: ['updated_at', 'UpdatedAt', 'fecha_actualizacion', 'FechaActualizacion']
  };
  
  // Aplicar mappings
  Object.keys(mappings).forEach(standardKey => {
    const variants = mappings[standardKey];
    const value = getValueFromVariants(normalized, variants);
    if (value !== undefined) {
      variants.forEach(variant => {
        normalized[variant] = value;
      });
    }
  });
  
  return normalized;
};

/**
 * Normalización específica para datos de usuario
 */
export const normalizeUsuarioData = (usuario) => {
  const normalized = { ...usuario };
  
  const mappings = {
    id: ['id', 'Id', 'usuario_id', 'UsuarioId', 'user_id', 'UserId'],
    clerk_id: ['clerk_id', 'ClerkId', 'clerkId'],
    nombre: ['nombre', 'Nombre', 'first_name', 'FirstName'],
    apellido: ['apellido', 'Apellido', 'last_name', 'LastName'],
    email: ['email', 'Email', 'correo', 'Correo'],
    telefono: ['telefono', 'Telefono', 'phone', 'Phone'],
    tipo_usuario: ['tipo_usuario', 'TipoUsuario', 'user_type', 'UserType'],
    empresa_id: ['empresa_id', 'EmpresaId', 'company_id', 'CompanyId'],
    activo: ['activo', 'Activo', 'active', 'Active']
  };
  
  Object.keys(mappings).forEach(standardKey => {
    const variants = mappings[standardKey];
    const value = getValueFromVariants(normalized, variants);
    if (value !== undefined) {
      variants.forEach(variant => {
        normalized[variant] = value;
      });
    }
  });
  
  return normalized;
};

/**
 * Normalización específica para datos de cliente
 */
export const normalizeClienteData = (cliente) => {
  const normalized = { ...cliente };
  
  const mappings = {
    id: ['id', 'Id', 'cliente_id', 'ClienteId', 'ClienteID'],
    nombre: ['nombre', 'Nombre', 'first_name', 'FirstName'],
    apellido: ['apellido', 'Apellido', 'last_name', 'LastName'],
    email: ['email', 'Email', 'correo', 'Correo'],
    telefono: ['telefono', 'Telefono', 'phone', 'Phone'],
    direccion: ['direccion', 'Direccion', 'address', 'Address'],
    rut: ['rut', 'Rut', 'RUT'],
    clerk_id: ['clerk_id', 'ClerkId'],
    activo: ['activo', 'Activo', 'active', 'Active']
  };
  
  Object.keys(mappings).forEach(standardKey => {
    const variants = mappings[standardKey];
    const value = getValueFromVariants(normalized, variants);
    if (value !== undefined) {
      variants.forEach(variant => {
        normalized[variant] = value;
      });
    }
  });
  
  // Agregar nombre completo si existe
  if (normalized.nombre && normalized.apellido) {
    const nombreCompleto = `${normalized.nombre} ${normalized.apellido}`.trim();
    normalized.nombreCompleto = nombreCompleto;
    normalized.NombreCompleto = nombreCompleto;
    normalized.full_name = nombreCompleto;
    normalized.FullName = nombreCompleto;
  }
  
  return normalized;
};

/**
 * Normalización específica para datos de vehículo
 */
export const normalizeVehiculoData = (vehiculo) => {
  const normalized = { ...vehiculo };
  
  const mappings = {
    patente: ['patente', 'Patente', 'license_plate', 'LicensePlate'],
    marca_local: ['marca_local', 'MarcaLocal', 'Marca', 'marca', 'brand', 'Brand'],
    modelo_local: ['modelo_local', 'ModeloLocal', 'Modelo', 'modelo', 'model', 'Model'],
    año: ['año', 'Año', 'year', 'Year'],
    color: ['color', 'Color'],
    vin: ['vin', 'VIN', 'Vin'],
    numero_motor: ['numero_motor', 'NumeroMotor', 'engine_number', 'EngineNumber'],
    numero_chasis: ['numero_chasis', 'NumeroChasis', 'chassis_number', 'ChassisNumber'],
    kilometraje: ['kilometraje', 'Kilometraje', 'mileage', 'Mileage'],
    cliente_id: ['cliente_id', 'ClienteId', 'ClienteID', 'Cliente'],
    activo: ['activo', 'Activo', 'active', 'Active']
  };
  
  Object.keys(mappings).forEach(standardKey => {
    const variants = mappings[standardKey];
    const value = getValueFromVariants(normalized, variants);
    if (value !== undefined) {
      variants.forEach(variant => {
        normalized[variant] = value;
      });
    }
  });
  
  return normalized;
};

/**
 * Normalización específica para datos de cita
 */
export const normalizeCitaData = (cita) => {
  const normalized = { ...cita };
  
  const mappings = {
    id: ['id', 'Id', 'cita_id', 'CitaId'],
    patente: ['patente', 'Patente'],
    cliente_id: ['cliente_id', 'ClienteId', 'ClienteID'],
    sucursal_id: ['sucursal_id', 'SucursalId', 'Sucursal'],
    fecha: ['fecha', 'Fecha', 'date', 'Date'],
    hora: ['hora', 'Hora', 'time', 'Time'],
    hora_fin: ['hora_fin', 'HoraFin', 'end_time', 'EndTime'],
    tiempo_total: ['tiempo_total', 'TiempoTotal', 'total_time', 'TotalTime'],
    motivo: ['motivo', 'Motivo', 'reason', 'Reason'],
    detalles: ['detalles', 'Detalles', 'details', 'Details'],
    estado_id: ['estado_id', 'EstadoId', 'status_id', 'StatusId']
  };
  
  Object.keys(mappings).forEach(standardKey => {
    const variants = mappings[standardKey];
    const value = getValueFromVariants(normalized, variants);
    if (value !== undefined) {
      variants.forEach(variant => {
        normalized[variant] = value;
      });
    }
  });
  
  return normalized;
};

/**
 * Normalización específica para datos de servicio
 */
export const normalizeServicioData = (servicio) => {
  const normalized = { ...servicio };
  
  const mappings = {
    id: ['id', 'Id', 'servicio_id', 'ServicioId'],
    nombre: ['nombre', 'Nombre', 'name', 'Name'],
    descripcion: ['descripcion', 'Descripcion', 'description', 'Description'],
    precio: ['precio', 'Precio', 'price', 'Price'],
    categoria_id: ['categoria_id', 'CategoriaId', 'CategoriaID', 'category_id', 'CategoryId'],
    tiempo_estimado: ['tiempo_estimado', 'TiempoEstimado', 'tiempoEstimado', 'estimated_time', 'EstimatedTime'],
    garantia_dias: ['garantia_dias', 'GarantiaDias', 'Garantia', 'garantia', 'warranty_days', 'WarrantyDays'],
    garantia_kilometros: ['garantia_kilometros', 'GarantiaKilometros', 'garantiaKilometros', 'warranty_km', 'WarrantyKm'],
    garantia_descripcion: ['garantia_descripcion', 'GarantiaDescripcion', 'garantiaDescripcion', 'warranty_description', 'WarrantyDescription'],
    sucursal_id: ['sucursal_id', 'SucursalId', 'SucursalID', 'sucursalId', 'branch_id', 'BranchId'],
    activo: ['activo', 'Activo', 'active', 'Active', 'disponible', 'Disponible']
  };
  
  Object.keys(mappings).forEach(standardKey => {
    const variants = mappings[standardKey];
    const value = getValueFromVariants(normalized, variants);
    if (value !== undefined) {
      variants.forEach(variant => {
        normalized[variant] = value;
      });
    }
  });
  
  return normalized;
};

/**
 * Normalización específica para datos de repuesto
 */
export const normalizeRepuestoData = (repuesto) => {
  const normalized = { ...repuesto };
  
  const mappings = {
    id: ['id', 'Id', 'repuesto_id', 'RepuestoId'],
    nombre: ['nombre', 'Nombre', 'name', 'Name'],
    descripcion: ['descripcion', 'Descripcion', 'description', 'Description'],
    codigo: ['codigo', 'Codigo', 'code', 'Code'],
    precio: ['precio', 'Precio', 'price', 'Price'],
    stock: ['stock', 'Stock'],
    stock_minimo: ['stock_minimo', 'StockMinimo', 'minimum_stock', 'MinimumStock'],
    categoria_id: ['categoria_id', 'CategoriaId', 'CategoriaID'],
    proveedor: ['proveedor', 'Proveedor', 'supplier', 'Supplier'],
    ubicacion: ['ubicacion', 'Ubicacion', 'location', 'Location'],
    activo: ['activo', 'Activo', 'active', 'Active', 'disponible', 'Disponible']
  };
  
  Object.keys(mappings).forEach(standardKey => {
    const variants = mappings[standardKey];
    const value = getValueFromVariants(normalized, variants);
    if (value !== undefined) {
      variants.forEach(variant => {
        normalized[variant] = value;
      });
    }
  });
  
  return normalized;
};

/**
 * Normalización específica para datos de mecánico
 */
export const normalizeMecanicoData = (mecanico) => {
  const normalized = { ...mecanico };
  
  const mappings = {
    id: ['id', 'Id', 'mecanico_id', 'MecanicoId'],
    nombre: ['nombre', 'Nombre', 'name', 'Name'],
    especialidad: ['especialidad', 'Especialidad', 'specialty', 'Specialty'],
    telefono: ['telefono', 'Telefono', 'phone', 'Phone'],
    email: ['email', 'Email'],
    sucursal_id: ['sucursal_id', 'SucursalId', 'Sucursal'],
    categoria_id: ['categoria_id', 'CategoriaId'],
    activo: ['activo', 'Activo', 'active', 'Active']
  };
  
  Object.keys(mappings).forEach(standardKey => {
    const variants = mappings[standardKey];
    const value = getValueFromVariants(normalized, variants);
    if (value !== undefined) {
      variants.forEach(variant => {
        normalized[variant] = value;
      });
    }
  });
  
  return normalized;
};

/**
 * Normalización específica para datos de sucursal
 */
export const normalizeSucursalData = (sucursal) => {
  const normalized = { ...sucursal };
  
  const mappings = {
    id: ['id', 'Id', 'sucursal_id', 'SucursalId'],
    nombre: ['nombre', 'Nombre', 'name', 'Name'],
    direccion: ['direccion', 'Direccion', 'address', 'Address'],
    telefono: ['telefono', 'Telefono', 'phone', 'Phone'],
    ciudad: ['ciudad', 'Ciudad', 'city', 'City'],
    horario_atencion: ['horario_atencion', 'HorarioAtencion', 'horarios', 'operating_hours', 'OperatingHours'],
    capacidad_clientes: ['capacidad_clientes', 'CapacidadClientes', 'client_capacity', 'ClientCapacity'],
    empresa_id: ['empresa_id', 'EmpresaId'],
    activo: ['activo', 'Activo', 'active', 'Active']
  };
  
  Object.keys(mappings).forEach(standardKey => {
    const variants = mappings[standardKey];
    const value = getValueFromVariants(normalized, variants);
    if (value !== undefined) {
      variants.forEach(variant => {
        normalized[variant] = value;
      });
    }
  });
  
  return normalized;
};

/**
 * Normalización genérica para cualquier tipo de dato
 */
export const normalizeGenericData = (data) => {
  const normalized = { ...data };
  
  // Mappings comunes que aplican a cualquier entidad
  const commonMappings = {
    id: ['id', 'Id', 'ID'],
    nombre: ['nombre', 'Nombre', 'name', 'Name'],
    activo: ['activo', 'Activo', 'active', 'Active'],
    created_at: ['created_at', 'CreatedAt', 'fecha_creacion', 'FechaCreacion'],
    updated_at: ['updated_at', 'UpdatedAt', 'fecha_actualizacion', 'FechaActualizacion']
  };
  
  Object.keys(commonMappings).forEach(standardKey => {
    const variants = commonMappings[standardKey];
    const value = getValueFromVariants(normalized, variants);
    if (value !== undefined) {
      variants.forEach(variant => {
        normalized[variant] = value;
      });
    }
  });
  
  return normalized;
};

/**
 * Obtiene el primer valor disponible de una lista de variantes de clave
 */
export const getValueFromVariants = (obj, variants) => {
  for (const variant of variants) {
    if (obj.hasOwnProperty(variant) && obj[variant] !== undefined) {
      return obj[variant];
    }
  }
  return undefined;
};

/**
 * Función helper para normalizar arrays de datos
 */
export const normalizeDataArray = (array, entityType = 'generic') => {
  if (!Array.isArray(array)) return array;
  return array.map(item => normalizeDataObject(item, entityType));
};

/**
 * Función helper para normalizar respuestas de API que incluyen paginación
 */
export const normalizeApiResponse = (response, entityType = 'generic') => {
  if (!response) return response;
  
  const normalized = { ...response };
  
  // Normalizar datos si existen
  if (normalized.data) {
    normalized.data = normalizeDataArray(normalized.data, entityType);
  }
  
  // Normalizar elementos individuales si es un objeto directo
  if (!normalized.data && !normalized.pagination && typeof response === 'object') {
    return normalizeDataObject(response, entityType);
  }
  
  return normalized;
};

/**
 * Función para limpiar y normalizar objetos antes de enviar a la API
 */
export const prepareForApi = (data, entityType = 'generic') => {
  if (!data) return data;
  
  // Normalizar primero
  const normalized = normalizeDataObject(data, entityType);
  
  // Limpiar campos undefined y null si es necesario
  const cleaned = {};
  Object.keys(normalized).forEach(key => {
    if (normalized[key] !== undefined) {
      cleaned[key] = normalized[key];
    }
  });
  
  return cleaned;
};

/**
 * Función para validar que un objeto tiene las propiedades mínimas requeridas
 */
export const validateRequiredFields = (data, requiredFields = []) => {
  if (!data || typeof data !== 'object') return false;
  
  return requiredFields.every(field => {
    const variants = generateKeyVariants(field);
    return variants.some(variant => data.hasOwnProperty(variant) && data[variant] !== undefined);
  });
};

/**
 * Exportar todas las funciones como un objeto para fácil importación
 */
const normalizationUtils = {
  convertNamingConvention,
  normalizeDataObject,
  normalizeDataArray,
  normalizeApiResponse,
  generateKeyVariants,
  applyEntitySpecificNormalization,
  normalizeEmpresaData,
  normalizeUsuarioData,
  normalizeClienteData,
  normalizeVehiculoData,
  normalizeCitaData,
  normalizeServicioData,
  normalizeRepuestoData,
  normalizeMecanicoData,
  normalizeSucursalData,
  normalizeGenericData,
  getValueFromVariants,
  prepareForApi,
  validateRequiredFields
};

export default normalizationUtils;